import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { logger } from '@marriott/mi-headless-utils';
// eslint-disable-next-line @nx/enforce-module-boundaries -- homepage GraphQL is the DTL data-access library
import { useGetRetargetingPropertyImage } from 'mi-homepage-graphql';
import { ProductDetailsCard } from '../ProductDetailsCard';
import {
  HOMEPAGE_HWS_RLM_USE_CASE,
  HOMEPAGE_RETARGETING_IMAGE_FALLBACK,
  RETARGETING_PRIMARY_IMAGE_FAILED_EVENT,
} from '../../utils/retargeting/retargeting.constants';
import { buildRetargetingCandidateKey } from '../../utils/retargeting/retargetingRuntime';
import type { RetargetingProductDetailsCardProps } from '../../utils/retargeting/retargeting.types';

const loggedCandidateFailuresByPage = new Map<string, Set<string>>();
const { log } = logger({})('RetargetingProductDetailsCard');

type ImageSource = 'supplied' | 'lookup' | 'uxl' | 'generic' | 'cf';
type FailureReason = 'missing_url' | 'load_error';
type FallbackOutcome = 'uxl_image' | 'generic_image' | 'authored_cf' | 'no_fallback';

type RetargetingProductDetailsCardComponentProps = {
  cardProps: RetargetingProductDetailsCardProps;
  fallback: React.ReactElement | null;
};

function getInitialSource(cardProps: RetargetingProductDetailsCardProps): ImageSource {
  const context = cardProps.retargetingImageContext;
  if (context?.suppliedImageUrl) return 'supplied';
  if (context?.useCase === HOMEPAGE_HWS_RLM_USE_CASE && context.marshaCode) return 'lookup';
  return 'generic';
}

function getInitialImageUrl(source: ImageSource, suppliedImageUrl?: string): string {
  if (source === 'supplied') return suppliedImageUrl ?? '';
  if (source === 'generic') return HOMEPAGE_RETARGETING_IMAGE_FALLBACK;
  return '';
}

function shouldLogCandidateFailure(pageViewId: string, candidateKey: string): boolean {
  const loggedCandidateFailures = loggedCandidateFailuresByPage.get(pageViewId) ?? new Set<string>();
  if (loggedCandidateFailures.has(candidateKey)) return false;
  loggedCandidateFailures.add(candidateKey);
  loggedCandidateFailuresByPage.set(pageViewId, loggedCandidateFailures);
  return true;
}

export function clearRetargetingImageFailureLogs(pageViewId: string): void {
  loggedCandidateFailuresByPage.delete(pageViewId);
}

export function RetargetingProductDetailsCard({
  cardProps,
  fallback,
}: RetargetingProductDetailsCardComponentProps): React.ReactElement | null {
  const context = cardProps.retargetingImageContext;
  const lookupPropertyImage = useGetRetargetingPropertyImage();
  const initialSource = getInitialSource(cardProps);
  const [source, setSource] = useState<ImageSource>(initialSource);
  const [imageUrl, setImageUrl] = useState(() => getInitialImageUrl(initialSource, context?.suppliedImageUrl));
  const failureReason = useRef<FailureReason>(context?.suppliedImageUrl ? 'load_error' : 'missing_url');
  const candidateKey = useMemo(() => buildRetargetingCandidateKey(cardProps), [cardProps]);

  const logFailure = useCallback(
    (fallbackOutcome: FallbackOutcome) => {
      if (!shouldLogCandidateFailure(context?.pageViewId ?? 'standalone', candidateKey)) return;
      log.warn(RETARGETING_PRIMARY_IMAGE_FAILED_EVENT, {
        useCase: context?.useCase ?? 'unknown',
        failureReason: failureReason.current,
        slotOrder: context.slotOrder,
        hasMarshaCode: Boolean(context?.marshaCode),
        fallbackOutcome,
      });
    },
    [candidateKey, context]
  );

  useEffect(() => {
    if (source !== 'lookup') return;

    let active = true;
    const marshaCode = context?.useCase === HOMEPAGE_HWS_RLM_USE_CASE ? context.marshaCode : undefined;

    if (!marshaCode) {
      setImageUrl(HOMEPAGE_RETARGETING_IMAGE_FALLBACK);
      setSource('generic');
      return;
    }

    lookupPropertyImage(marshaCode).then(url => {
      if (!active) return;
      if (url) {
        setImageUrl(url);
        setSource('uxl');
      } else {
        setImageUrl(HOMEPAGE_RETARGETING_IMAGE_FALLBACK);
        setSource('generic');
      }
    });

    return () => {
      active = false;
    };
  }, [context?.marshaCode, context?.useCase, lookupPropertyImage, source]);

  const handleImageLoad = useCallback(() => {
    if (source === 'uxl') logFailure('uxl_image');
    if (source === 'generic') logFailure('generic_image');
  }, [logFailure, source]);

  useEffect(() => {
    if (source !== 'cf') return;
    logFailure(fallback ? 'authored_cf' : 'no_fallback');
  }, [fallback, logFailure, source]);

  const handleImageError = useCallback(() => {
    if (source === 'supplied') {
      failureReason.current = 'load_error';
      setImageUrl('');
      setSource(context?.useCase === HOMEPAGE_HWS_RLM_USE_CASE && context.marshaCode ? 'lookup' : 'generic');
      if (context?.useCase !== HOMEPAGE_HWS_RLM_USE_CASE || !context.marshaCode) {
        setImageUrl(HOMEPAGE_RETARGETING_IMAGE_FALLBACK);
      }
      return;
    }

    if (source === 'uxl') {
      setImageUrl(HOMEPAGE_RETARGETING_IMAGE_FALLBACK);
      setSource('generic');
      return;
    }

    if (source === 'generic') {
      setSource('cf');
    }
  }, [context?.marshaCode, context?.useCase, source]);

  if (source === 'cf') return fallback;

  return (
    <ProductDetailsCard
      {...cardProps}
      fileReferenceImage={imageUrl}
      onImageLoad={handleImageLoad}
      onImageError={handleImageError}
    />
  );
}
